import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const BASE = process.env.PUBLIC_URL || "";

const PRINCIPLES = [
  {
    n: "01",
    title: "Answer the phone.",
    body:
      "The first rule, and the one we still measure everything against. A real voice, morning or night, from someone who knows your building.",
  },
  {
    n: "02",
    title: "Charge one number.",
    body:
      "Eight percent flat. Trade invoices at cost. If it's not on the statement in a plain sentence, we didn\u2019t do it.",
  },
  {
    n: "03",
    title: "Keep the list short.",
    body:
      "We manage buildings we\u2019d live in. When we\u2019re at capacity, we say so. Growth is a choice, not a metric.",
  },
  {
    n: "04",
    title: "Sweat the small things.",
    body:
      "Fresh flowers on the counter. Local coffee in the cupboard. A note by the door. Hospitality is 100 tiny decisions.",
  },
];

const NUMBERS = [
  { n: "2019", l: "Founded in Bridgeland, one duplex at a time" },
  { n: "36", l: "Homes on the list — capped by choice" },
  { n: "3 days", l: "Average time-to-lease across the portfolio" },
  { n: "9.6", l: "Resident satisfaction, out of ten" },
];

const TEAM = [
  {
    name: "Elliot Kwan",
    role: "Founder · Portfolio",
    bio:
      "Grew up between Kensington and the Bow. Formerly built restaurants; now builds building-shaped ones.",
    initials: "EK",
  },
  {
    name: "Sana Wilder",
    role: "Head of Residents",
    bio:
      "Answers the phone. Knows every doorbell tone in the portfolio. Trained as an architect.",
    initials: "SW",
  },
  {
    name: "Marc DuFresne",
    role: "Design & Programming",
    bio:
      "Picks the paint, the pendants, and the playlist. Bridgeland loft #3 is his fault.",
    initials: "MD",
  },
  {
    name: "Priya Kohli",
    role: "Owner Relations",
    bio:
      "Writes the Monday email. Ex-CPA. Believes a statement is a love letter, in the right hands.",
    initials: "PK",
  },
  {
    name: "Ben Ostrowski",
    role: "Field & Maintenance",
    bio:
      "Trades ticket-master. Knows a good caulking bead when he sees one. Ski patroller weekends.",
    initials: "BO",
  },
  {
    name: "Rowan Chevalier",
    role: "Photography & Story",
    bio:
      "Takes the pictures. Writes the listings. Once got a moose to hold still.",
    initials: "RC",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* ============ 1. EDITORIAL HERO ============ */}
      <header
        className="relative overflow-hidden"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
        data-testid="about-hero"
      >
        <div className="relative mx-auto max-w-[1440px] px-6 pt-[128px] md:px-10 md:pt-[168px]">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12 md:gap-12">
            {/* left: eyebrow + title */}
            <div className="md:col-span-7">
              <Reveal>
                <Eyebrow>Our story</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display mt-7 max-w-[16ch]"
                  style={{
                    fontSize: "clamp(48px, 7.6vw, 108px)",
                    color: "#201E1A",
                    lineHeight: 0.95,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="about-title"
                >
                  A rental company that{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    stayed small.
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="body-measure mt-8 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(32,30,26,0.75)" }}
                  data-testid="about-intro"
                >
                  ROAM started with one building in Bridgeland and a simple
                  rule: answer the phone. Six years on, we manage a short list
                  of exceptional homes across Alberta — and the rule hasn&rsquo;t
                  budged.
                </p>
              </Reveal>
            </div>

            {/* right: meta card */}
            <div className="md:col-span-5">
              <Reveal delay={0.15}>
                <dl
                  className="grid grid-cols-2 gap-x-6 gap-y-8 border-t pt-8"
                  style={{ borderColor: "rgba(32,30,26,0.16)" }}
                  data-testid="about-meta"
                >
                  <div>
                    <dt
                      className="label-caps text-[11px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      Est.
                    </dt>
                    <dd
                      className="font-display mt-2"
                      style={{ fontSize: 34, color: "#24382E", lineHeight: 1 }}
                    >
                      2019
                    </dd>
                  </div>
                  <div>
                    <dt
                      className="label-caps text-[11px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      Based
                    </dt>
                    <dd
                      className="font-display mt-2"
                      style={{ fontSize: 34, color: "#24382E", lineHeight: 1 }}
                    >
                      Calgary, AB
                    </dd>
                  </div>
                  <div>
                    <dt
                      className="label-caps text-[11px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      Homes
                    </dt>
                    <dd
                      className="font-display mt-2"
                      style={{ fontSize: 34, color: "#24382E", lineHeight: 1 }}
                    >
                      36 · capped
                    </dd>
                  </div>
                  <div>
                    <dt
                      className="label-caps text-[11px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      People
                    </dt>
                    <dd
                      className="font-display mt-2"
                      style={{ fontSize: 34, color: "#24382E", lineHeight: 1 }}
                    >
                      Six &mdash; on purpose
                    </dd>
                  </div>
                </dl>
              </Reveal>
            </div>
          </div>

          {/* wide editorial image */}
          <Reveal delay={0.2} className="mt-16 md:mt-20">
            <figure className="relative overflow-hidden" style={{ borderRadius: 20 }}>
              <img
                src={`${BASE}/hero/img5.png`}
                alt="A ROAM home at dusk in the foothills west of Calgary"
                className="block w-full object-cover"
                style={{ aspectRatio: "21 / 9", maxHeight: 640 }}
                data-testid="about-hero-image"
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(20,35,27,0) 60%, rgba(20,35,27,0.35) 100%)",
                }}
              />
              <figcaption
                className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full px-4 py-2"
                style={{
                  backgroundColor: "rgba(248,245,239,0.9)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <span
                  className="rounded-full"
                  style={{ width: 8, height: 8, backgroundColor: "#C89A4E" }}
                />
                <span className="label-caps text-[11px]" style={{ color: "#24382E" }}>
                  Highwood Meadow · a real ROAM home
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
        <div className="mt-16 w-full opacity-80" aria-hidden="true">
          <Ridgeline stroke="#C89A4E" height={90} />
        </div>
      </header>

      {/* ============ 2. FOUNDING NARRATIVE ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
        data-testid="about-story"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <Reveal>
                <Eyebrow>The beginning</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <p
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(40px, 5vw, 68px)",
                    color: "#201E1A",
                    lineHeight: 0.98,
                  }}
                >
                  One duplex.
                  <br />
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    One phone.
                  </span>
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-7 md:col-start-6">
              <Reveal>
                <p
                  className="text-[17px] leading-[1.7] md:text-[18px]"
                  style={{ color: "rgba(32,30,26,0.82)" }}
                  data-testid="about-story-para-1"
                >
                  In the summer of 2019, Elliot bought a hundred-year-old
                  duplex two blocks from Tom Campbell&rsquo;s Hill. The furnace
                  went out on the first tenant walkthrough. He picked up the
                  phone, drove over, and stayed until it was warm.
                </p>
              </Reveal>
              <Reveal delay={0.08}>
                <p
                  className="mt-6 text-[17px] leading-[1.7] md:text-[18px]"
                  style={{ color: "rgba(32,30,26,0.82)" }}
                >
                  That night he wrote down the rule that still runs the
                  company: <em>answer the phone.</em> No answering machine, no
                  &ldquo;submit a ticket,&rdquo; no queue. If it was worth
                  ringing about, it was worth showing up for.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p
                  className="mt-6 text-[17px] leading-[1.7] md:text-[18px]"
                  style={{ color: "rgba(32,30,26,0.82)" }}
                >
                  Six years later, we&rsquo;re six people, thirty-six homes,
                  and one very old rotary phone that sits on the reception
                  desk on 11th Ave. It still rings. We still pick up.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <blockquote
                  className="mt-14 border-l pl-6"
                  style={{ borderColor: "#C89A4E" }}
                  data-testid="about-founder-quote"
                >
                  <p
                    className="font-display italic"
                    style={{
                      fontSize: "clamp(24px, 2.6vw, 34px)",
                      color: "#24382E",
                      lineHeight: 1.25,
                    }}
                  >
                    &ldquo;A rental should feel like a favour from a friend
                    with good taste — not a transaction with a stranger.&rdquo;
                  </p>
                  <footer
                    className="label-caps mt-5 text-[11px]"
                    style={{ color: "rgba(32,30,26,0.6)" }}
                  >
                    Elliot Kwan &mdash; Founder
                  </footer>
                </blockquote>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 3. PRINCIPLES ============ */}
      <section
        className="grain relative"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
        data-testid="about-principles"
      >
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
            <div className="md:col-span-5">
              <Reveal>
                <Eyebrow color="#AEC4C7">How we work</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h2
                  className="font-display mt-6 max-w-[14ch]"
                  style={{
                    fontSize: "clamp(38px, 5vw, 68px)",
                    color: "#F8F5EF",
                    lineHeight: 1,
                  }}
                >
                  Four principles.{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    That&rsquo;s the plan.
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-8 max-w-[36ch] text-[16px] md:text-[17px]"
                  style={{ color: "rgba(237,233,224,0.72)" }}
                >
                  No slide decks. No corporate mission statements. Just four
                  things we do — plainly, every day, with no exceptions.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-7">
              <ol className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
                {PRINCIPLES.map((p, i) => (
                  <Reveal
                    key={p.n}
                    as="li"
                    delay={0.04 * i}
                    className={i % 2 === 1 ? "sm:mt-14" : ""}
                  >
                    <div
                      className="border-t pt-5"
                      style={{ borderColor: "rgba(174,196,199,0.28)" }}
                      data-testid={`about-principle-${p.n}`}
                    >
                      <p
                        className="font-display"
                        style={{
                          fontSize: 24,
                          color: "#C89A4E",
                          lineHeight: 1,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {p.n}
                      </p>
                      <h3
                        className="font-display mt-3"
                        style={{
                          fontSize: "clamp(24px, 2.4vw, 32px)",
                          color: "#F8F5EF",
                          lineHeight: 1.05,
                        }}
                      >
                        {p.title}
                      </h3>
                      <p
                        className="mt-3 text-[15px] leading-[1.6]"
                        style={{ color: "rgba(237,233,224,0.72)" }}
                      >
                        {p.body}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 4. NUMBERS BAND ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
        data-testid="about-numbers"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <Reveal>
            <Eyebrow>By the numbers</Eyebrow>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-10 md:grid-cols-4 md:gap-12">
            {NUMBERS.map((n, i) => (
              <Reveal
                key={n.n}
                delay={i * 0.04}
                className={i % 2 === 1 ? "md:mt-10" : ""}
              >
                <div className="border-t pt-5" style={{ borderColor: "rgba(32,30,26,0.16)" }}>
                  <p
                    className="font-display"
                    style={{
                      fontSize: "clamp(40px, 4.5vw, 64px)",
                      color: "#24382E",
                      lineHeight: 0.9,
                    }}
                  >
                    {n.n}
                  </p>
                  <p
                    className="mt-3 text-[14px] md:text-[15px]"
                    style={{ color: "rgba(32,30,26,0.7)" }}
                  >
                    {n.l}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5. TEAM ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
        data-testid="about-team"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-6">
              <Reveal>
                <Eyebrow>The people</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h2
                  className="font-display mt-6 max-w-[18ch]"
                  style={{
                    fontSize: "clamp(38px, 5vw, 68px)",
                    color: "#201E1A",
                    lineHeight: 1,
                  }}
                >
                  Six of us.{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    We plan to keep it that way.
                  </span>
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={0.1}>
                <p
                  className="text-[16px] leading-[1.7] md:text-[17px]"
                  style={{ color: "rgba(32,30,26,0.75)" }}
                >
                  Everyone here talks to owners, meets residents, and gets
                  their hands on the buildings. There is no back office. There
                  is barely a front office.
                </p>
              </Reveal>
            </div>
          </div>

          <ul
            className="mt-16 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="about-team-grid"
          >
            {TEAM.map((m, i) => (
              <Reveal key={m.name} as="li" delay={i * 0.04}>
                <article
                  className="group"
                  data-testid={`about-team-${m.initials.toLowerCase()}`}
                >
                  {/* portrait tile */}
                  <div
                    className="relative overflow-hidden"
                    style={{
                      borderRadius: 14,
                      aspectRatio: "4 / 5",
                      backgroundColor: "#EDE9E0",
                    }}
                  >
                    <div
                      className="absolute inset-0 grid place-items-center transition-transform duration-700 group-hover:scale-[1.03]"
                      style={{
                        background:
                          "radial-gradient(120% 90% at 30% 20%, #F6EFDD 0%, #E4DFD3 45%, #C7BEAD 100%)",
                      }}
                      aria-hidden
                    >
                      <span
                        className="font-display"
                        style={{
                          fontSize: "clamp(48px, 6.4vw, 92px)",
                          color: "#24382E",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {m.initials}
                      </span>
                    </div>
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(20,35,27,0) 60%, rgba(20,35,27,0.15) 100%)",
                      }}
                    />
                    <div
                      className="absolute bottom-3 left-3 rounded-full px-2.5 py-1"
                      style={{
                        backgroundColor: "rgba(248,245,239,0.9)",
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      <span
                        className="label-caps text-[10px]"
                        style={{ color: "#24382E" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>

                  <h3
                    className="font-display mt-5"
                    style={{
                      fontSize: 24,
                      color: "#201E1A",
                      lineHeight: 1.1,
                    }}
                  >
                    {m.name}
                  </h3>
                  <p
                    className="label-caps mt-1 text-[11px]"
                    style={{ color: "#C89A4E" }}
                  >
                    {m.role}
                  </p>
                  <p
                    className="mt-4 text-[15px] leading-[1.6]"
                    style={{ color: "rgba(32,30,26,0.72)" }}
                  >
                    {m.bio}
                  </p>
                </article>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 6. RESIDENT PULL-QUOTE ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
        data-testid="about-quote-band"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-28">
          <Reveal>
            <p
              className="font-display max-w-[22ch]"
              style={{
                fontSize: "clamp(30px, 4vw, 54px)",
                color: "#201E1A",
                lineHeight: 1.06,
              }}
            >
              &ldquo;They fixed the dishwasher, replaced the doormat, and left
              a{" "}
              <span className="italic" style={{ color: "#C89A4E" }}>
                jar of honey.
              </span>{" "}
              I&rsquo;ve never wanted to renew a lease more.&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <p
              className="label-caps mt-8 text-[11px]"
              style={{ color: "rgba(32,30,26,0.6)" }}
            >
              &mdash; Naomi &amp; Chris · residents, Marda Loop · since 2022
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ 7. CLOSING CTA ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
        data-testid="about-cta"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-8">
              <Reveal>
                <Eyebrow color="#C89A4E">Come by the office</Eyebrow>
              </Reveal>
              <Reveal delay={0.06}>
                <h2
                  className="font-display mt-6 max-w-[16ch]"
                  style={{
                    fontSize: "clamp(40px, 6vw, 88px)",
                    color: "#F8F5EF",
                    lineHeight: 0.98,
                  }}
                >
                  Or just call.{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    Someone will pick up.
                  </span>
                </h2>
              </Reveal>
            </div>
            <div className="flex flex-wrap items-center gap-4 md:col-span-4 md:justify-end">
              <Reveal delay={0.1}>
                <Link
                  to="/contact"
                  className="pill pill-gold text-base"
                  data-testid="about-cta-primary"
                >
                  Say hello
                </Link>
              </Reveal>
              <Reveal delay={0.14}>
                <Link
                  to="/residences"
                  className="group inline-flex items-center gap-2 text-[15px]"
                  style={{ color: "#F8F5EF" }}
                  data-testid="about-cta-secondary"
                >
                  <span className="label-caps text-[13px]">
                    See the homes
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
